'use client';

import { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from '@/components/i18n-provider';
import { ComputeShareCard } from '@/components/wallet/compute-share-card';
import { useAuth } from '@/components/auth-provider';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import {
  Wallet as WalletIcon,
  CreditCard,

  ExternalLink,
  Plus,
  Sparkles,
  AlertTriangle,
  History,
  Key,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Filter,
  ShieldCheck,
  Trash2,
  Edit3,
  X,
  Search,
  Ban,
  Gift,
  Users,
  RefreshCw,
  Gauge,
  HardDrive,
  Database,
  Download,
  Eye,
  EyeOff,
} from 'lucide-react';
import { AddCreditsDialog } from '@/components/add-credits-dialog';
import { CreatorRewardsCard } from '@/components/wallet/creator-rewards-card';
import { useEarningsSummary } from '@/components/wallet/use-earnings-summary';
import { openPlansDialog } from '@/components/plans-dialog-host';
import { displayPlanName } from '@/lib/plans/display';
import { formatAmount, formatCredits, cleanDescription, toDollars } from '@/lib/balance/units';
import {
  groupActivity,
  todayKey,
  yesterdayKey,
  BYOK_TYPE_SET,
  HOLD_TYPE_SET,
  EARNINGS_BALANCE_TYPE_SET,
  type ActivityEvent,
} from '@/lib/balance/activity';
import { PanelBackground } from '@/components/panel-bg';
import { Avatar } from '@/components/avatar';
import { Tip } from '@/components/ui/tooltip';
import { Spinner } from '@/components/ui/spinner';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
// The `...Credits` names are what GET /api/balance answers as of 2026-09-20
// (1 credit = $1, stateable); the route still also sends the legacy
// `...Dollars` names, which this view no longer reads.
interface BalanceSummary {
  totalBalance: string;
  lockedBalance: string;
  planBalance: string;
  availableBalance: string;
  totalCredits: number;
  planCredits: number;
  purchasedCredits: number;
  availableCredits: number;
  /** Monthly plan allowance (the quota meter's denominator); null when there
   * is no active subscription or no grant has landed yet. */
  planGrantCredits: number | null;
  /** ISO date the plan credits reset; null when unknown. */
  planRenewsAt: string | null;
  demoCredits: number;
  demoMaxCredits: number;
  lowBalanceWarning: boolean;
  hasStripeCustomer: boolean;
  planName: string | null;
  subscriptionStatus: string | null;
}

interface TransactionItem {
  id: string;
  amount: string;
  amountCredits: number;
  type: string;
  description: string | null;
  relatedTokenId: string | null;
  relatedChargeId: string | null;
  createdAt: string;
}

interface LockItem {
  id: string;
  agentId: string;
  agentUsername: string | null;
  agentDisplayName: string | null;
  agentAccountType?: string;
  agentAvatarUrl?: string | null;
  amount: string;
  settledAmount: string;
  amountCredits: number;
  settledCredits: number;
  status: string;
  createdAt: string;
}

interface TokenItem {
  id: string;
  initialBalance: string;
  availableBalance: string;
  reservedBalance: string;
  initialCredits: number;
  availableCredits: number;
  settledCredits: number;
  status: string;
  balanceSource: string;
  expiresAt: string;
  createdAt: string;
  locks: LockItem[];
}

export type WalletTab = 'overview' | 'activity' | 'tokens' | 'spending' | 'referrals';

interface RecentReferral {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  createdAt: string;
}

interface ReferralStats {
  totalReferrals: number;
  referralCode: string;
  shareUrl: string;
  pendingRewards: number;
  completedRewards: number;
  totalRewardsEarned: number;
  recentReferrals: RecentReferral[];
}

/* ------------------------------------------------------------------ */
/*  Human-friendly type labels                                         */
/* ------------------------------------------------------------------ */
const TYPE_LABEL_KEYS: Record<string, string> = {
  charge_sent: 'wallet.typeUsageFee',
  charge_received: 'wallet.typeIncome',
  a2a_earning: 'wallet.typeIncome',
  token_lock: 'wallet.typeFundsReserved',
  token_unlock: 'wallet.typeFundsReleased',
  token_expired: 'wallet.typeTokenExpired',
  token_topup: 'wallet.typeTopUp',
  deposit: 'wallet.typeDeposit',
  withdrawal: 'wallet.typeWithdrawal',
  subscription: 'wallet.typeSubscription',
  referral: 'wallet.typeReferralBonus',
  transfer: 'wallet.typeTransfer',
  byok_llm: 'wallet.typeByokLlm',
  byok_mcp_sampling: 'wallet.typeByokMcp',
  // Earnings ledger types. Without these the row fell through to the raw
  // enum name and the history literally read "attribution payout".
  attribution_payout: 'wallet.typeAppUsageReward',
  creator_grant: 'wallet.typeCreatorFundGrant',
  earnings_adjustment: 'wallet.typeAdjustment',
  // Commission legs of a settlement (platform fee, agent commission). Tied
  // for the most common type in the ledger; used to render as the raw enum.
  commission_received: 'wallet.typeCommission',
  // Qualified referral rewards credited as Creator Rewards (migration 0051);
  // legacy 'referral' rows above keep the "Referral bonus" label.
  referral_earning: 'wallet.typeReferralReward',
};

function friendlyType(type: string, t: (key: string) => string): string {
  const key = TYPE_LABEL_KEYS[type];
  return key ? t(key) : type.replace(/_/g, ' ');
}

/** Description worth showing: cleaned, and not just the label repeated
 * ("Token expired · Token expired"). */
function displayDescription(tx: { description: string | null }, label: string): string | null {
  const desc = cleanDescription(tx.description);
  if (!desc) return null;
  return desc.toLowerCase() === label.toLowerCase() ? null : desc;
}

type TxFilter = 'all' | 'charge_sent' | 'charge_received' | 'rewards' | 'deposit' | 'subscription' | string;
const TX_FILTERS: { key: TxFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'wallet.filterAll' },
  { key: 'charge_sent', labelKey: 'wallet.filterSpent' },
  { key: 'charge_received', labelKey: 'wallet.filterEarned' },
  { key: 'rewards', labelKey: 'wallet.filterRewards' },
  { key: 'deposit', labelKey: 'wallet.filterDeposits' },
  { key: 'subscription', labelKey: 'wallet.filterSubscriptions' },
];

type TxRange = 'all' | '30d' | '90d' | 'year';
const TX_RANGES: { key: TxRange; labelKey: string }[] = [
  { key: 'all', labelKey: 'wallet.rangeAll' },
  { key: '30d', labelKey: 'wallet.range30' },
  { key: '90d', labelKey: 'wallet.range90' },
  { key: 'year', labelKey: 'wallet.rangeYear' },
];

function rangeFrom(range: TxRange): string | null {
  if (range === 'all') return null;
  const d = new Date();
  if (range === '30d') {
    d.setDate(d.getDate() - 30);
  } else if (range === '90d') {
    d.setDate(d.getDate() - 90);
  } else {
    d.setMonth(0, 1);
    d.setHours(0, 0, 0, 0);
  }
  return d.toISOString();
}

const PAGE_SIZE = 20;

/* ------------------------------------------------------------------ */
/*  Animated counter — rolls from previous value to new value          */
/* ------------------------------------------------------------------ */
function AnimatedAmount({ value, highlight, className }: { value: number; highlight?: boolean; className?: string }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    prevRef.current = value;
    if (from === to) { setDisplay(to); return; }

    const duration = 900;
    const start = performance.now();

    function tick(now: number) {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(from + (to - from) * ease);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value]);

  return (
    <span className={cn(
      'tabular-nums transition-colors duration-700 inline-block',
      highlight && 'text-primary scale-105',
      className,
    )}>
      {formatAmount(display)}
    </span>
  );
}


/* ------------------------------------------------------------------ */
/*  Wallet view                                                        */
/* ------------------------------------------------------------------ */
/**
 * Core wallet view. The standalone `/wallet` page is now a 307 redirect
 * to `/workspace?wallet=1` so this component is effectively always
 * rendered embedded inside the workspace's full-screen dialog system
 * — `embedded` still controls outer chrome (`max-w-4xl mx-auto`,
 * `PanelBackground`, `<h1>`) so a non-embedded mount (tests, dev
 * preview, future surface) still looks right.
 *
 * Both modes now run the same `useSearchParams()` payment-return
 * pipeline: `FullscreenNavProvider` opens the dialog from
 * `?wallet=1` and threads `payment`/`session_id`/`plan_switched`
 * into the dialog href, where this component picks them up to run
 * verify-payment + balance poll + URL cleanup.
 */
export function WalletView({
  embedded = false,
  activeTab,
  onTabChange,
  hideNav = false,
}: {
  embedded?: boolean;
  /** Controlled active tab. When set, the parent owns tab selection. */
  activeTab?: WalletTab;
  /** Fired on any internal tab switch (nav click, "View all", token links). */
  onTabChange?: (tab: WalletTab) => void;
  /** Hide the built-in sidebar nav (parent renders a unified one). */
  hideNav?: boolean;
} = {}) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center py-12"><Spinner size="2xl" tone="muted" /></div>}>
      <WalletViewContent
        embedded={embedded}
        controlledTab={activeTab}
        onTabChange={onTabChange}
        hideNav={hideNav}
      />
    </Suspense>
  );
}

function WalletViewContent({
  embedded = false,
  controlledTab,
  onTabChange,
  hideNav = false,
}: {
  embedded?: boolean;
  controlledTab?: WalletTab;
  onTabChange?: (tab: WalletTab) => void;
  hideNav?: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const router = useRouter();
  // Same gate CreatorRewardsCard applies internally, read here so the credits
  // card can span full width when there is no rewards bucket to pair with.
  // The hook de-duplicates the /api/earnings/summary call across every mount.
  const { summary: earningsSummary } = useEarningsSummary();
  const showRewardsCard = !!earningsSummary
    && (earningsSummary.hasEarningsRows || earningsSummary.hasPublishedApps);
  // useSearchParams isn't useful when this view is embedded inside the
  // workspace dialog (the dialog doesn't own the route), so we resolve
  // it conditionally and treat the embedded case as "no payment params
  // to apply". The hook is still called unconditionally on the page
  // route so React's rules of hooks hold.
  const searchParams = useSearchParams();

  const [internalTab, setInternalTab] = useState<WalletTab>('overview');
  // Controlled when the parent (AccountView's unified nav) passes a tab;
  // otherwise self-managed. Either way `setActiveTab` is the single entry
  // point so internal cross-tab links ("View all", token refs) keep
  // working and notify the parent.
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: WalletTab) => {
    onTabChange?.(tab);
    if (controlledTab === undefined) setInternalTab(tab);
  };
  const [navOpen, setNavOpen] = useState(false);
  const [balance, setBalance] = useState<BalanceSummary | null>(null);
  // Plan card only exists for subscribers. A non-subscriber has no monthly
  // allowance, so rendering an empty quota meter would invent a bucket.
  const showPlanCard = !!balance && (balance.subscriptionStatus === 'active' || balance.planCredits > 0);
  // Next monthly grant: how much gets ADDED and when. Needs both the amount and
  // the date to say anything useful, so it degrades to a plain "rolls over"
  // line if either is missing (no Stripe key, cache miss, fresh plan switch).
  const nextGrant = balance && balance.planGrantCredits && balance.planGrantCredits > 0 && balance.planRenewsAt
    ? {
        amount: balance.planGrantCredits,
        date: new Date(balance.planRenewsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      }
    : null;
  /**
   * AI usage as a PERCENTAGE (the unit-less Usage card). Prefers the monthly
   * plan allowance; falls back to the daily demo allowance for free accounts;
   * null when the account has neither, which the card renders as
   * pay-as-you-go instead of a fake denominator.
   */
  /**
   * Escrow currently held against in-flight agent work (users.locked_balance,
   * the REAL bucket — plan/demo tokens debit their bucket at mint instead).
   * Surfaced because it is the only reason spendable can be lower than the
   * balance; the per-token/lock detail behind it is support tooling, not
   * consumer UI (2026-08-09: the Payment Tokens tab left the nav).
   */
  const heldDollars = balance ? toDollars(BigInt(balance.lockedBalance)) : 0;
  /** The token/lock inspector stays reachable for support, not for users. */
  const isAdmin = user?.role === 'admin';
  const aiMeterPct = (() => {
    if (!balance) return null;
    if (balance.subscriptionStatus === 'active' && balance.planGrantCredits && balance.planGrantCredits > 0) {
      const used = Math.min(Math.max(balance.planGrantCredits - balance.planCredits, 0), balance.planGrantCredits);
      return (used / balance.planGrantCredits) * 100;
    }
    if (balance.demoMaxCredits > 0) {
      const used = Math.min(Math.max(balance.demoMaxCredits - balance.demoCredits, 0), balance.demoMaxCredits);
      return (used / balance.demoMaxCredits) * 100;
    }
    return null;
  })();
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  // "Add Credits" opens the top-up dialog (amount → checkout). The Plans
  // upsell lives behind its "view plans" link, not the primary CTA.
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [highlightCard, setHighlightCard] = useState<'available' | 'plan' | null>(null);
  const [activityKey, setActivityKey] = useState(0);
  // Unit-less usage meters (entitlements-plan.md W7): percentages only, no
  // seconds/bytes/keys in consumer UI. AI has its own meter on the plan card.
  const [usageMeters, setUsageMeters] = useState<
    { compute: { pct: number }; storage: { pct: number }; data: { pct: number } } | null
  >(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/user/usage')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.meters) setUsageMeters(d.meters); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // Transactions
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txFilter, setTxFilter] = useState<TxFilter>('all');
  const [txRange, setTxRange] = useState<TxRange>('all');
  const [txShowHolds, setTxShowHolds] = useState(false);
  const [txOffset, setTxOffset] = useState(0);
  const [txHasMore, setTxHasMore] = useState(false);
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);
  // Days are collapsed to their summary line by default (today starts open);
  // this records only the days the user toggled away from that default.
  const [toggledDays, setToggledDays] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Tokens
  const [tokens, setTokens] = useState<TokenItem[]>([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [expandedTokenId, setExpandedTokenId] = useState<string | null>(null);

  // Spending overrides
  interface SpendingOverrideItem {
    id: string;
    targetAgentId: string | null;
    agentUsername?: string;
    agentDisplayName?: string;
    autoApproveThreshold: string;
    autoApproveThresholdCredits: number;
    maxPerRequest: string | null;
    maxPerRequestCredits: number | null;
    maxDaily: string | null;
    maxDailyCredits: number | null;
    computeSharePct: number | null;
    overrideType: string;
    source: string | null;
    isEnabled: boolean;
    expiresAt: string | null;
    createdAt: string;
  }
  const [overrides, setOverrides] = useState<SpendingOverrideItem[]>([]);
  const [overridesLoading, setOverridesLoading] = useState(false);

  // Spending rule form state
  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  const [ruleFormEditId, setRuleFormEditId] = useState<string | null>(null);
  const [ruleFormSaving, setRuleFormSaving] = useState(false);
  const [ruleFormError, setRuleFormError] = useState<string | null>(null);
  const [ruleAgentQuery, setRuleAgentQuery] = useState('');
  const [ruleAgentId, setRuleAgentId] = useState<string | null>(null);
  const [ruleAgentResults, setRuleAgentResults] = useState<{ id: string; username: string; displayName: string | null }[]>([]);
  const [ruleAgentSearching, setRuleAgentSearching] = useState(false);
  const [ruleAutoApprove, setRuleAutoApprove] = useState('0.50');
  const [ruleMaxPerRequest, setRuleMaxPerRequest] = useState('');
  const [ruleMaxDaily, setRuleMaxDaily] = useState('');
  const [ruleComputeShare, setRuleComputeShare] = useState('');
  const [ruleType, setRuleType] = useState<'permanent' | 'block'>('permanent');
  const agentSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Referral state
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [referralConfig, setReferralConfig] = useState<{
    levels: { level: number; name: string; rate: number; rateLabel: string }[];
    qualificationDays: number;
    rewardType: string;
  } | null>(null);

  const fetchReferralStats = useCallback(async () => {
    if (!user) return;
    setLoadingReferrals(true);
    try {
      const res = await fetch('/api/referrals/stats');
      if (res.ok) {
        const data = await res.json();
        setReferralStats(data);
      }
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setLoadingReferrals(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'referrals') {
      fetchReferralStats();
      if (!referralConfig) {
        fetch('/api/referrals/config').then(r => r.json()).then(setReferralConfig).catch(() => {});
      }
    }
  }, [activeTab, fetchReferralStats, referralConfig]);

  const copyReferralLink = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedReferral(true);
      setTimeout(() => setCopiedReferral(false), 2000);
    } catch {}
  };

  const fetchBalance = useCallback(async () => {
    try {
      const r = await fetch('/api/balance');
      const data = await r.json();
      setBalance(data);
    } catch { /* ignore */ }
  }, []);

  // -- Balance fetch + verify-and-apply on payment return --
  useEffect(() => {
    let cancelled = false;
    // Stripe returns now land on `/workspace?wallet=1&payment=success&…`
    // and `FullscreenNavProvider` forwards those params into the
    // dialog href before it strips them from the browser URL. The
    // embedded variant therefore runs the same verify-payment /
    // balance-poll / highlight pipeline as the standalone page used to
    // — there's no longer a reason to short-circuit on `embedded`.
    const paymentOk = searchParams.get('payment') === 'success';
    const sessionId = searchParams.get('session_id');
    const planSwitched = searchParams.get('plan_switched') === 'true';

    if (paymentOk) {
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      url.searchParams.delete('session_id');
      url.searchParams.delete('plan_switched');
      router.replace(url.pathname + url.search, { scroll: false });
    }

    (async () => {
      // 1. Fetch current balance
      let data: BalanceSummary | null = null;
      try {
        const r = await fetch('/api/balance');
        data = await r.json();
      } catch { /* ignore */ }
      if (cancelled) return;
      setBalance(data);
      setBalanceLoading(false);
      if (!paymentOk || !data) return;

      // Plan switch: credits already applied server-side, just highlight
      if (planSwitched) {
        setHighlightCard('plan');
        setTimeout(() => setHighlightCard(null), 3000);
        return;
      }

      const baseAvail = data.availableCredits ?? 0;
      const basePlan = data.planCredits ?? 0;

      function highlightChanged(fresh: BalanceSummary) {
        const card = fresh.availableCredits > baseAvail ? 'available'
          : fresh.planCredits > basePlan ? 'plan'
          : null;
        if (card) {
          setHighlightCard(card);
          setTimeout(() => setHighlightCard(null), 3000);
        }
        return card !== null;
      }

      // 2. If we have the session ID, verify & apply credits server-side (fallback for missing webhook)
      if (sessionId) {
        try {
          await fetch('/api/stripe/verify-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
        } catch { /* best-effort */ }
      }

      // 3. Re-fetch balance after verify (credits should be applied now)
      try {
        const r = await fetch('/api/balance');
        const fresh = await r.json();
        if (cancelled) return;
        setBalance(fresh);
        if (highlightChanged(fresh)) return;
      } catch { /* ignore */ }

      // 4. If still unchanged, poll a few more times (webhook may arrive with delay)
      if (cancelled) return;
      let attempts = 0;
      const poll = setInterval(async () => {
        if (cancelled) { clearInterval(poll); return; }
        attempts++;
        try {
          const r = await fetch('/api/balance');
          const fresh = await r.json();
          if (cancelled) return;
          const changed = fresh.availableCredits > baseAvail || fresh.planCredits > basePlan;
          if (changed || attempts >= 8) {
            clearInterval(poll);
            setBalance(fresh);
            highlightChanged(fresh);
          }
        } catch { /* ignore */ }
      }, 3000);
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Fetch transactions when activity tab or filter changes --
  useEffect(() => {
    if (activeTab !== 'activity') return;
    setTxLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE + 1), offset: String(txOffset) });
    if (txFilter !== 'all') params.set('type', txFilter);
    if (txShowHolds) params.set('holds', '1');
    const from = rangeFrom(txRange);
    if (from) params.set('from', from);
    fetch(`/api/balance/transactions?${params}`)
      .then((r) => r.json())
      .then((data) => {
        const items: TransactionItem[] = data.transactions ?? [];
        setTxHasMore(items.length > PAGE_SIZE);
        setTransactions(items.slice(0, PAGE_SIZE));
      })
      .finally(() => setTxLoading(false));
  }, [activeTab, txFilter, txRange, txShowHolds, txOffset]);

  // -- Fetch tokens --
  useEffect(() => {
    if (activeTab !== 'tokens') return;
    setTokensLoading(true);
    fetch('/api/balance/tokens')
      .then((r) => r.json())
      .then((data) => setTokens(data.tokens ?? []))
      .finally(() => setTokensLoading(false));
  }, [activeTab]);

  // -- Fetch spending overrides + active agent spending --
  interface AgentSpending {
    agentId: string;
    agentUsername: string;
    agentAccountType?: string;
    agentAvatarUrl?: string | null;
    totalLocked: number;
    totalSettled: number;
    tokenCount: number;
  }
  const [agentSpending, setAgentSpending] = useState<AgentSpending[]>([]);
  useEffect(() => {
    if (activeTab !== 'spending') return;
    setOverridesLoading(true);
    Promise.all([
      fetch('/api/balance/spending-overrides').then(r => r.json()),
      fetch('/api/balance/tokens').then(r => r.json()),
    ])
      .then(([overridesData, tokensData]) => {
        setOverrides(overridesData.overrides ?? []);
        const allTokens: TokenItem[] = tokensData.tokens ?? [];
        const byAgent = new Map<string, AgentSpending>();
        for (const token of allTokens) {
          if (token.status !== 'active') continue;
          for (const lock of token.locks) {
            const key = lock.agentId;
            const existing = byAgent.get(key);
            if (existing) {
              existing.totalLocked += lock.amountCredits;
              existing.totalSettled += lock.settledCredits;
              existing.tokenCount += 1;
            } else {
              byAgent.set(key, {
                agentId: lock.agentId,
                agentUsername: lock.agentUsername ?? lock.agentId.slice(0, 8),
                totalLocked: lock.amountCredits,
                totalSettled: lock.settledCredits,
                tokenCount: 1,
              });
            }
          }
        }
        setAgentSpending(Array.from(byAgent.values()).sort((a, b) => b.totalLocked - a.totalLocked));
      })
      .finally(() => setOverridesLoading(false));
  }, [activeTab]);

  const handleDeleteOverride = async (id: string) => {
    setOverrides(prev => prev.filter(o => o.id !== id));
    try {
      await fetch(`/api/balance/spending-overrides/${id}`, { method: 'DELETE' });
    } catch { /* ignore */ }
  };

  const handleToggleOverride = async (id: string, enabled: boolean) => {
    setOverrides(prev => prev.map(o => o.id === id ? { ...o, isEnabled: enabled } : o));
    try {
      await fetch(`/api/balance/spending-overrides/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isEnabled: enabled }),
      });
    } catch { /* ignore */ }
  };

  const resetRuleForm = useCallback(() => {
    setRuleFormOpen(false);
    setRuleFormEditId(null);
    setRuleFormError(null);
    setRuleAgentQuery('');
    setRuleAgentId(null);
    setRuleAgentResults([]);
    setRuleAutoApprove('0.50');
    setRuleMaxPerRequest('');
    setRuleMaxDaily('');
    setRuleComputeShare('');
    setRuleType('permanent');
  }, []);

  const openCreateForm = useCallback(() => {
    resetRuleForm();
    setRuleFormOpen(true);
  }, [resetRuleForm]);

  const openEditForm = useCallback((override: SpendingOverrideItem) => {
    setRuleFormEditId(override.id);
    setRuleFormError(null);
    setRuleAgentQuery(override.agentUsername ? `@${override.agentUsername}` : '');
    setRuleAgentId(override.targetAgentId);
    setRuleAgentResults([]);
    setRuleAutoApprove(String(override.autoApproveThresholdCredits ?? 0.50));
    setRuleMaxPerRequest(override.maxPerRequestCredits != null ? String(override.maxPerRequestCredits) : '');
    setRuleMaxDaily(override.maxDailyCredits != null ? String(override.maxDailyCredits) : '');
    setRuleComputeShare(override.computeSharePct != null ? String(override.computeSharePct) : '');
    setRuleType(override.overrideType === 'block' ? 'block' : 'permanent');
    setRuleFormOpen(true);
  }, []);

  const searchAgents = useCallback((query: string) => {
    setRuleAgentQuery(query);
    setRuleAgentId(null);

    if (agentSearchTimeout.current) clearTimeout(agentSearchTimeout.current);
    const clean = query.replace(/^@/, '').trim();
    if (!clean) {
      setRuleAgentResults([]);
      return;
    }
    agentSearchTimeout.current = setTimeout(async () => {
      setRuleAgentSearching(true);
      try {
        const res = await fetch(`/api/discovery/agents?search=${encodeURIComponent(clean)}&limit=5`);
        const data = await res.json();
        setRuleAgentResults((data.agents ?? []).map((a: { id: string; username: string; displayName: string | null }) => ({
          id: a.id, username: a.username, displayName: a.displayName,
        })));
      } catch { setRuleAgentResults([]); }
      setRuleAgentSearching(false);
    }, 300);
  }, []);

  const handleSaveRule = async () => {
    setRuleFormSaving(true);
    setRuleFormError(null);
    try {
      const autoApprove = parseFloat(ruleAutoApprove);
      if (isNaN(autoApprove) || autoApprove < 0) {
        setRuleFormError(t('wallet.errAutoApprove'));
        setRuleFormSaving(false);
        return;
      }
      const maxPR = ruleMaxPerRequest ? parseFloat(ruleMaxPerRequest) : null;
      if (maxPR !== null && (isNaN(maxPR) || maxPR < 0)) {
        setRuleFormError(t('wallet.errMaxPerReq'));
        setRuleFormSaving(false);
        return;
      }
      const maxD = ruleMaxDaily ? parseFloat(ruleMaxDaily) : null;
      if (maxD !== null && (isNaN(maxD) || maxD < 0)) {
        setRuleFormError(t('wallet.errMaxDaily'));
        setRuleFormSaving(false);
        return;
      }
      const sharePct = ruleComputeShare ? parseInt(ruleComputeShare, 10) : null;
      if (sharePct !== null && (isNaN(sharePct) || sharePct < 1 || sharePct > 100)) {
        setRuleFormError(t('wallet.errComputeShare'));
        setRuleFormSaving(false);
        return;
      }

      if (ruleFormEditId) {
        const res = await fetch(`/api/balance/spending-overrides/${ruleFormEditId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            autoApproveThreshold: autoApprove,
            maxPerRequest: maxPR,
            maxDaily: maxD,
            computeSharePct: sharePct,
            overrideType: ruleType,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setRuleFormError(data.error ?? t('wallet.errUpdateRule'));
          setRuleFormSaving(false);
          return;
        }
      } else {
        const clean = ruleAgentQuery.replace(/^@/, '').trim();
        const res = await fetch('/api/balance/spending-overrides', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetAgentUsername: clean || undefined,
            autoApproveThreshold: autoApprove,
            maxPerRequest: maxPR,
            maxDaily: maxD,
            computeSharePct: sharePct,
            overrideType: ruleType,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setRuleFormError(data.error ?? t('wallet.errCreateRule'));
          setRuleFormSaving(false);
          return;
        }
      }

      resetRuleForm();
      setOverridesLoading(true);
      fetch('/api/balance/spending-overrides')
        .then((r) => r.json())
        .then((data) => setOverrides(data.overrides ?? []))
        .finally(() => setOverridesLoading(false));
    } catch {
      setRuleFormError(t('wallet.errGeneric'));
    }
    setRuleFormSaving(false);
  };

  // -- Handlers --
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      // Customer Portal returns land on the workspace with the wallet
      // dialog auto-opened via `?wallet=1` — same pattern as Checkout.
      const returnUrl = typeof window !== 'undefined' ? `${window.location.origin}/workspace?wallet=1` : '';
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } finally {
      setPortalLoading(false);
    }
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // (Spending limit handlers removed — per-token limits are in Settings/Tokens)

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <WalletIcon className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">{t('wallet.title')}</h1>
        <p className="text-muted-foreground mb-6">{t('wallet.signInToManage')}</p>
        <Link href="/login" className="btn-primary">{t('auth.signIn')}</Link>
      </div>
    );
  }

  const tabs: { id: WalletTab; label: string; icon: typeof WalletIcon }[] = [
    { id: 'overview', label: t('wallet.tabBalance'), icon: WalletIcon },
    { id: 'activity', label: t('wallet.tabActivity'), icon: History },
    // Payment Tokens is a read-only inspector over the escrow layer (token
    // uuids, raw statuses, per-agent locks) with no user-facing control, so
    // it is support tooling rather than a wallet section. The money fact a
    // user actually needs from it — what is on hold — lives on the balance
    // card instead.
    ...(isAdmin ? [{ id: 'tokens' as const, label: t('wallet.tabTokens'), icon: Key }] : []),
    { id: 'spending', label: t('wallet.tabSpending'), icon: ShieldCheck },
    { id: 'referrals', label: t('wallet.tabReferrals'), icon: Gift },
  ];

  const showLowBalance = balance && balance.lowBalanceWarning && (balance.availableCredits + balance.planCredits) < 0.02;

  return (
    <div className={embedded ? 'w-full' : 'max-w-4xl mx-auto'}>
      {!embedded && <PanelBackground />}
      {!embedded && <h1 className="text-2xl font-bold mb-6">{t('wallet.title')}</h1>}

      <div className="flex flex-col @lg/main:flex-row gap-6">
        {/* Sidebar tabs — dropdown overlay on mobile, list on desktop.
            Suppressed when embedded in AccountView, which renders one
            unified (single-level) nav across wallet + analytics + admin. */}
        {!hideNav && (
        <nav className="@lg/main:w-48 shrink-0 relative">
          {(() => {
            const active = tabs.find(t => t.id === activeTab);
            const ActiveIcon = active?.icon ?? WalletIcon;
            return (
              <button
                onClick={() => setNavOpen(o => !o)}
                className="@lg/main:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border border-border bg-card shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" />
                  {active?.label ?? t('wallet.title')}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${navOpen ? 'rotate-180' : ''}`} />
              </button>
            );
          })()}
          <div className={`${navOpen ? 'block' : 'hidden'} @lg/main:block @lg/main:relative @lg/main:mt-0 @lg/main:shadow-none @lg/main:border-0 @lg/main:bg-transparent @lg/main:rounded-none @lg/main:max-h-none absolute left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg max-h-[60vh] overflow-y-auto space-y-0.5 p-1 @lg/main:p-0`}>
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </nav>
        )}

        {/* Content */}
        <div className="flex-1">

          {/* ---- Overview / Balance ---- */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Balance cards */}
              {balanceLoading ? (
                <div className="card p-6 animate-pulse">
                  <div className="h-6 bg-muted rounded w-1/3 mb-4" />
                  <div className="h-10 bg-muted rounded w-1/2" />
                </div>
              ) : balance ? (
                <>
                  {showLowBalance && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                      <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400 flex-1">
                        {t('wallet.lowBalanceWarning')}
                      </p>
                      <button type="button" onClick={() => setShowAddCredits(true)} className="btn-primary text-sm px-3 py-1 flex-shrink-0">
                        {t('wallet.addCredits')}
                      </button>
                    </div>
                  )}

                  {/* THREE STAT TILES + a usage strip (2026-08-09 layout
                      decision, option B). One tile per bucket, one number and
                      one action each:
                        Plan      — the plan NAME is the hero, not a number:
                                    plan-credit consumption already renders as
                                    the AI meter below, so a credits headline
                                    here would show the same figure twice.
                        Purchased — the spendable balance and Add credits.
                        Rewards   — CreatorRewardsCard in tile form; hides
                                    itself for plain consumers, and the grid
                                    drops to two columns to match.
                      Bucket disclosures (12-month validity, non-withdrawable)
                      stay in the Add Credits dialog, where the decision is
                      actually made. */}
                  <div className={`grid grid-cols-1 @lg/main:grid-cols-2 ${showRewardsCard ? '@3xl/main:grid-cols-3' : ''} gap-4`}>
                    <div className={`card p-5 flex flex-col transition-all duration-700 ${highlightCard === 'plan' ? 'ring-2 ring-primary/50 bg-primary/5' : ''}`}>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        {showPlanCard ? <RefreshCw className="h-4 w-4 shrink-0" /> : <WalletIcon className="h-4 w-4 shrink-0" />}
                        <span className="text-xs font-medium">{t('wallet.planLabel')}</span>
                      </div>
                      {/* The hero is the PLAN NAME, so it must always BE a
                          name. No `planName` means no subscription — the
                          account is on Free even when it still holds plan
                          credits from a lapsed period, which is why the
                          fallback is the Free label and not the bucket label
                          ("Plan credits" read as a heading next to a user
                          menu already saying "Free"). */}
                      <p className="text-2xl font-bold mt-2 truncate">
                        {balance.planName ? displayPlanName(balance.planName) : t('wallet.freePlanName')}
                      </p>
                      {/* Consumption as a BAR, not a sentence (tile diet):
                          the used-of-granted numbers live in the bar's
                          geometry; the caption keeps only the reset date. */}
                      {showPlanCard && nextGrant ? (
                        <>
                          <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{
                                width: `${Math.max(0, Math.min(100, ((nextGrant.amount - Math.max(nextGrant.amount - balance.planCredits, 0)) / Math.max(nextGrant.amount, 0.01)) * 100))}%`,
                              }}
                            />
                          </div>
                          <p className="mt-1.5 text-xs tabular-nums text-muted-foreground">
                            {t('wallet.planNextGrant', { amount: formatAmount(nextGrant.amount), date: nextGrant.date })}
                          </p>
                        </>
                      ) : (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {showPlanCard ? t('wallet.planRollsOver') : t('wallet.freePlanDesc')}
                        </p>
                      )}
                      <div className="mt-auto pt-3">
                        {balance.hasStripeCustomer && balance.planName ? (
                          <button type="button" onClick={handleManageSubscription} disabled={portalLoading} className="btn-secondary inline-flex items-center gap-1.5 text-xs px-2.5 py-1">
                            {portalLoading ? <Spinner size="md" /> : <ExternalLink className="h-3.5 w-3.5" />}
                            {t('wallet.manage')}
                          </button>
                        ) : (
                          <button type="button" onClick={() => openPlansDialog()} className="btn-primary inline-flex items-center gap-1.5 text-xs px-2.5 py-1">
                            <Sparkles className="h-3.5 w-3.5" />
                            {t('wallet.upgrade')}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className={`card p-5 flex flex-col transition-all duration-700 ${highlightCard === 'available' ? 'ring-2 ring-primary/50 bg-primary/5' : ''}`}>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <WalletIcon className="h-4 w-4 shrink-0" />
                        <span className="text-xs font-medium">{t('wallet.purchasedCredits')}</span>
                      </div>
                      <p className="text-2xl font-bold mt-2">
                        <AnimatedAmount value={balance.purchasedCredits} highlight={highlightCard === 'available'} />
                        <span className="text-sm font-medium text-muted-foreground">{' '}{t('wallet.creditsUnit')}</span>
                      </p>
                      {heldDollars > 0 && (
                        <Tip label={t('wallet.onHoldDesc')}>
                          <p className="text-xs text-muted-foreground mt-1 underline decoration-dotted underline-offset-2 cursor-help w-fit tabular-nums">
                            {t('wallet.onHold')} · {formatAmount(heldDollars)}
                          </p>
                        </Tip>
                      )}
                      <div className="mt-auto pt-3">
                        <button type="button" onClick={() => setShowAddCredits(true)} className="btn-secondary inline-flex items-center gap-1.5 text-xs px-2.5 py-1">
                          {t('wallet.addCredits')}
                        </button>
                      </div>
                    </div>

                    {showRewardsCard && <CreatorRewardsCard variant="tile" />}
                  </div>

                  {/* Unit-less usage meters (W7): AI / Compute / Storage /
                      Data as a full-width strip. AI measures the monthly plan
                      allowance when there is one (plan credits expire at
                      renewal, so used-of-granted is a true statement), else
                      the daily demo allowance, else it says pay-as-you-go
                      rather than inventing a denominator. */}
                  <div className="card p-5">
                    <p className="text-sm font-medium text-muted-foreground mb-3">{t('wallet.usage')}</p>
                    <div className="grid grid-cols-1 @md/main:grid-cols-2 @xl/main:grid-cols-4 gap-x-6 gap-y-3">
                      {([
                        { key: 'ai', label: t('wallet.usageAi'), Icon: Sparkles, pct: aiMeterPct },
                        { key: 'compute', label: t('wallet.usageCompute'), Icon: Gauge, pct: usageMeters?.compute.pct ?? null },
                        { key: 'storage', label: t('wallet.usageStorage'), Icon: HardDrive, pct: usageMeters?.storage.pct ?? null },
                        { key: 'data', label: t('wallet.usageData'), Icon: Database, pct: usageMeters?.data.pct ?? null },
                      ] as const).map(({ key, label, Icon, pct }) => {
                        const clamped = pct == null ? 0 : Math.max(0, Math.min(100, pct));
                        const state = pct == null
                          ? (key === 'ai' ? t('wallet.usagePayAsYouGo') : t('wallet.usagePlenty'))
                          : pct >= 100
                            ? t('wallet.usageFull')
                            : pct >= 80
                              ? t('wallet.usageLow')
                              : t('wallet.usagePlenty');
                        return (
                          <div key={key}>
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <span className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
                                <Icon className="h-4 w-4 shrink-0" />
                                <span className="truncate">{label}</span>
                              </span>
                              <span className="text-[11px] text-muted-foreground shrink-0">{state}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className={`h-full transition-all ${clamped >= 100 ? 'bg-destructive' : clamped >= 80 ? 'bg-amber-500' : 'bg-primary'}`}
                                style={{ width: `${clamped}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent Activity preview */}
                  <RecentActivity key={activityKey} onViewAll={() => setActiveTab('activity')} />
                </>
              ) : (
                <div className="card p-6 text-center text-muted-foreground">
                  <WalletIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('wallet.unableToLoadBalance')}</p>
                </div>
              )}
            </div>
          )}

          {/* ---- Activity / Transactions ---- */}
          {activeTab === 'activity' && (() => {
            const exportParams = new URLSearchParams();
            if (txFilter !== 'all') exportParams.set('type', txFilter);
            if (txShowHolds) exportParams.set('holds', '1');
            const exportFrom = rangeFrom(txRange);
            if (exportFrom) exportParams.set('from', exportFrom);
            const exportQs = exportParams.toString();
            const exportHref = `/api/balance/transactions/export${exportQs ? `?${exportQs}` : ''}`;
            const now = new Date();
            const tKey = todayKey(now);
            const yKey = yesterdayKey(now);
            const thisYear = now.getFullYear();
            const signed = (v: number) => (v === 0 ? formatAmount(0) : `${v > 0 ? '+' : ''}${formatAmount(v)}`);
            const amountTone = (v: number) => (v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : 'text-muted-foreground');
            return (
            <div className="card p-4 @md/main:p-6">
              <div className="mb-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold">{t('wallet.activity')}</h2>
                  <div className="flex items-center gap-2">
                    <select
                      className="text-xs rounded-md border border-border bg-background px-1.5 py-1 text-muted-foreground"
                      value={txRange}
                      onChange={(e) => { setTxRange(e.target.value as TxRange); setTxOffset(0); }}
                      aria-label={t('wallet.rangeAll')}
                    >
                      {TX_RANGES.map(({ key, labelKey }) => (
                        <option key={key} value={key}>{t(labelKey)}</option>
                      ))}
                    </select>
                    <a
                      href={exportHref}
                      download
                      className="flex items-center gap-1 text-xs rounded-md border border-border px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted whitespace-nowrap"
                    >
                      <Download className="h-3 w-3" />
                      {t('wallet.exportCsv')}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mb-1 scrollbar-none">
                  <Filter className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  {TX_FILTERS.map(({ key, labelKey }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setTxFilter(key); setTxOffset(0); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                        txFilter === key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                  <span className="flex-1 min-w-2" />
                  <button
                    type="button"
                    onClick={() => { setTxShowHolds(!txShowHolds); setTxOffset(0); }}
                    aria-pressed={txShowHolds}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      txShowHolds ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {txShowHolds ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {t('wallet.showHolds')}
                  </button>
                </div>
              </div>

              {txLoading && transactions.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Spinner size="xl" tone="muted" />
                </div>
              ) : transactions.length === 0 ? (
                <p className="text-muted-foreground text-sm py-6">{t('wallet.noTransactions')}</p>
              ) : (
                <>
                  <ul className="space-y-0">
                    {groupActivity(transactions).map((day) => {
                      const dayOpen = toggledDays[day.dayKey] ?? day.dayKey === tKey;
                      return (
                      <li key={day.dayKey}>
                        <button
                          type="button"
                          onClick={() => setToggledDays((prev) => ({ ...prev, [day.dayKey]: !dayOpen }))}
                          aria-expanded={dayOpen}
                          className="w-full flex items-baseline justify-between gap-2 pt-3 pb-1.5 border-b border-border/70 text-left group"
                        >
                          <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground group-hover:text-foreground">
                            {dayOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            {day.dayKey === tKey
                              ? t('wallet.dayToday')
                              : day.dayKey === yKey
                              ? t('wallet.dayYesterday')
                              : day.date.toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  ...(day.date.getFullYear() !== thisYear ? { year: 'numeric' } : {}),
                                })}
                            {!dayOpen && (
                              <span className="font-normal text-muted-foreground/80">
                                &middot; {t('wallet.entriesCount', { count: day.events.length })}
                              </span>
                            )}
                          </span>
                          <span className={`text-[11px] tabular-nums ${amountTone(day.net)}`}>
                            {t('wallet.dayNet', { amount: signed(day.net) })}
                          </span>
                        </button>
                        {dayOpen && (
                        <ul className="divide-y divide-border/50">
                          {day.events.map((ev: ActivityEvent) => {
                            const tx = ev.primary;
                            const grouped = ev.txs.length > 1;
                            const isExpanded = expandedTxId === ev.key;
                            const hasDetails = grouped || !!(tx.relatedTokenId || tx.relatedChargeId);
                            const isByok = ev.hasByok;
                            const label = friendlyType(tx.type, t);
                            const desc = displayDescription(tx, label);
                            const amount = grouped ? ev.net : tx.amountCredits;
                            return (
                              <li key={ev.key} className={`text-sm ${isByok ? 'bg-primary/5 border-l-2 border-l-primary/40 -ml-1 pl-1' : ''}`}>
                                <div className="flex items-center justify-between py-2.5 gap-2">
                                  <div className="min-w-0 flex-1 flex items-center gap-2">
                                    {hasDetails ? (
                                      <button type="button" onClick={() => setExpandedTxId(isExpanded ? null : ev.key)} className="p-0.5 rounded text-muted-foreground hover:text-foreground" aria-expanded={isExpanded}>
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </button>
                                    ) : (
                                      <span className="w-5" />
                                    )}
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {isByok && (
                                          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-primary/20 text-primary shrink-0">
                                            BYOK
                                          </span>
                                        )}
                                        <span className="text-foreground font-medium">{label}</span>
                                        {desc && <span className="text-muted-foreground text-xs">&middot; {desc}</span>}
                                        {EARNINGS_BALANCE_TYPE_SET.has(tx.type) && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                                            {t('wallet.chipRewards')}
                                          </span>
                                        )}
                                        {HOLD_TYPE_SET.has(tx.type) && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                                            {t('wallet.chipHold')}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {new Date(tx.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                        {grouped && <> &middot; {t('wallet.entriesCount', { count: ev.txs.length })}</>}
                                      </p>
                                    </div>
                                  </div>
                                  <span
                                    className={`whitespace-nowrap font-medium flex-shrink-0 tabular-nums ${isByok ? 'text-muted-foreground' : amountTone(amount)}`}
                                    title={isByok ? undefined : formatCredits(amount)}
                                  >
                                    {isByok ? <span className="text-xs">&mdash;</span> : signed(amount)}
                                  </span>
                                </div>
                                {isExpanded && hasDetails && (
                                  <div className="pb-3 pl-7 pr-2 space-y-1.5 text-xs text-muted-foreground bg-muted/30 rounded-b-lg -mx-1 px-3 py-2">
                                    {grouped && ev.txs.map((leg) => {
                                      const legLabel = friendlyType(leg.type, t);
                                      const legDesc = displayDescription(leg, legLabel);
                                      return (
                                        <div key={leg.id} className="flex items-center justify-between gap-2">
                                          <span className="min-w-0 truncate">
                                            {legLabel}
                                            {legDesc && <span className="text-muted-foreground/80"> &middot; {legDesc}</span>}
                                          </span>
                                          <span className={`tabular-nums flex-shrink-0 ${amountTone(leg.amountCredits)}`}>
                                            {signed(leg.amountCredits)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {tx.relatedTokenId && isAdmin && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-foreground/80">{t('wallet.tokenLabel')}</span>
                                        <button type="button" onClick={() => { setActiveTab('tokens'); setExpandedTokenId(tx.relatedTokenId); }} className="text-primary hover:underline truncate max-w-[200px]">{tx.relatedTokenId.slice(0, 12)}...</button>
                                        <button type="button" onClick={() => copyText(tx.relatedTokenId!, `token-${tx.id}`)} className="p-1 rounded hover:bg-muted">
                                          {copiedId === `token-${tx.id}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                        </button>
                                      </div>
                                    )}
                                    {tx.relatedChargeId && (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-foreground/80">{t('wallet.chargeLabel')}</span>
                                        <code className="truncate max-w-[200px]">{tx.relatedChargeId}</code>
                                        <button type="button" onClick={() => copyText(tx.relatedChargeId!, `charge-${tx.id}`)} className="p-1 rounded hover:bg-muted">
                                          {copiedId === `charge-${tx.id}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                    <button type="button" onClick={() => setTxOffset(Math.max(0, txOffset - PAGE_SIZE))} disabled={txOffset === 0} className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none">{t('wallet.previous')}</button>
                    <span className="text-xs text-muted-foreground">{txOffset + 1}&ndash;{txOffset + transactions.length}</span>
                    <button type="button" onClick={() => setTxOffset(txOffset + PAGE_SIZE)} disabled={!txHasMore} className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none">{t('wallet.next')}</button>
                  </div>
                </>
              )}
            </div>
            );
          })()}

          {/* ---- Tokens ---- */}
          {activeTab === 'tokens' && (
            <div className="card p-4 @md/main:p-6">
              <h2 className="text-lg font-semibold mb-1">{t('wallet.paymentTokens')}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t('wallet.paymentTokensDesc')}</p>

              {tokensLoading ? (
                <div className="flex justify-center py-8"><Spinner size="xl" tone="muted" /></div>
              ) : tokens.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">{t('wallet.noActiveTokens')}</p>
              ) : (
                <ul className="space-y-3">
                  {tokens.map((token) => {
                    const isExpanded = expandedTokenId === token.id;
                    return (
                      <li key={token.id} className="border border-border/50 rounded-lg overflow-hidden">
                        <button type="button" onClick={() => setExpandedTokenId(isExpanded ? null : token.id)} className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-2 min-w-0">
                            {isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                            <Tip label={token.id}><span className="font-mono text-xs truncate max-w-[180px]">{token.id.slice(0, 8)}...</span></Tip>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${token.status === 'active' ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>{token.status}</span>
                          </div>
                          <span className="text-sm text-muted-foreground flex-shrink-0">{formatAmount(token.availableCredits)} / {formatAmount(token.initialCredits)} {t('wallet.cr')}</span>
                        </button>
                        {isExpanded && (
                          <div className="border-t border-border/50 bg-muted/20 px-4 py-3 space-y-2 text-sm">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                              <span>{t('wallet.source')} {token.balanceSource}</span>
                              <span>{t('wallet.expires')} {new Date(token.expiresAt).toLocaleDateString()}</span>
                              <span>{t('wallet.created')} {new Date(token.createdAt).toLocaleDateString()}</span>
                            </div>
                            {token.locks.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-foreground/80 mb-1">{t('wallet.locks')} ({token.locks.length})</p>
                                <ul className="space-y-1.5 text-xs">
                                  {token.locks.map((lock) => (
                                    <li key={lock.id} className="flex items-center justify-between gap-2 pl-2 border-l-2 border-border/50">
                                      <span>@{lock.agentUsername ?? lock.agentId.slice(0, 8)}</span>
                                      <span className="text-muted-foreground">{formatAmount(lock.settledCredits)} / {formatAmount(lock.amountCredits)} &middot; {lock.status}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'spending' && (
            <div className="space-y-4">
              {/* Platform defaults */}
              <div className="card p-4 @md/main:p-5 bg-muted/30 border-dashed">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{t('wallet.platformDefaults')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('wallet.platformDefaultsDesc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Active spending */}
              {agentSpending.length > 0 && (
                <div className="card p-4 @md/main:p-6">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {t('wallet.activeSpending')}
                  </h3>
                  <div className="space-y-0 divide-y divide-border/50">
                    {agentSpending.map((a) => {
                      const hasOverride = overrides.some(o => o.targetAgentId === a.agentId);
                      const isAgent = a.agentAccountType === 'agent';
                      return (
                        <div key={a.agentId} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar
                              seed={a.agentUsername}
                              type={isAgent ? 'agent' : 'human'}
                              src={a.agentAvatarUrl}
                              className="h-7 w-7 shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Link href={`/u/${a.agentUsername}`} className="text-sm font-medium hover:underline truncate" onClick={(e) => e.stopPropagation()}>
                                  @{a.agentUsername}
                                </Link>
                                <span className={cn(
                                  'text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider',
                                  isAgent ? 'bg-emerald-500/10 text-emerald-600' : 'bg-blue-500/10 text-blue-600',
                                )}>
                                  {isAgent ? t('wallet.agentPrefix') : t('wallet.userPrefix')}
                                </span>
                              </div>
                              <div className="flex gap-3 text-xs text-muted-foreground mt-0.5 tabular-nums">
                                <span>{formatAmount(a.totalLocked)} {t('wallet.locked')}</span>
                                <span>{formatAmount(a.totalSettled)} {t('wallet.settled')}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {hasOverride ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-600 font-medium">{t('wallet.hasRule')}</span>
                            ) : (
                              <button
                                onClick={() => {
                                  resetRuleForm();
                                  setRuleAgentQuery(`@${a.agentUsername}`);
                                  setRuleAgentId(a.agentId);
                                  setRuleFormOpen(true);
                                }}
                                className="text-[10px] px-2 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors font-medium"
                              >
                                + {t('wallet.addRule')}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Apps' daily compute — the app-first quota card. Raises here
                  and the share-block notification CTAs run the same verbs. */}
              <ComputeShareCard />

              {/* Spending Rules (money only — compute moved to the card above) */}
              <div className="card p-4 @md/main:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">{t('wallet.spendingRulesTitle')}</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('wallet.spendingRulesDesc')}
                  </p>
                </div>
                {!ruleFormOpen && (
                  <button
                    onClick={openCreateForm}
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('wallet.addRule')}
                  </button>
                )}
              </div>

              {ruleFormOpen && (
                <div className="border border-border rounded-lg p-4 mb-4 bg-muted/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold">{ruleFormEditId ? t('wallet.editRuleTitle') : t('wallet.newRuleTitle')}</h3>
                    <button onClick={resetRuleForm} className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:bg-accent transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {!ruleFormEditId ? (
                      <div className="relative">
                        <label className="text-xs font-medium text-foreground/80 mb-1.5 block">{t('wallet.targetAgent')}</label>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                          <input
                            type="text"
                            value={ruleAgentQuery}
                            onChange={(e) => searchAgents(e.target.value)}
                            placeholder={t('wallet.targetAgentPlaceholder')}
                            className="w-full pl-8 pr-8 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                          {ruleAgentSearching && <Spinner size="sm" tone="muted" className="absolute right-2.5 top-1/2 -translate-y-1/2" />}
                        </div>
                        {ruleAgentResults.length > 0 && !ruleAgentId && (
                          <ul className="absolute z-10 w-full mt-1 border border-border rounded-md bg-background shadow-lg max-h-40 overflow-y-auto">
                            {ruleAgentResults.map((a) => (
                              <li key={a.id}>
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                                  onClick={() => {
                                    setRuleAgentQuery(`@${a.username}`);
                                    setRuleAgentId(a.id);
                                    setRuleAgentResults([]);
                                  }}
                                >
                                  <span className="font-medium">@{a.username}</span>
                                  {a.displayName && <span className="ml-2 text-muted-foreground">{a.displayName}</span>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs font-medium text-foreground/80 mb-1 block">{t('wallet.targetAgent')}</label>
                        <p className="text-sm text-muted-foreground">{ruleAgentQuery || t('wallet.allAgentsFallback')}</p>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-medium text-foreground/80 mb-1.5 block">{t('wallet.ruleType')}</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setRuleType('permanent')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors ${
                            ruleType === 'permanent'
                              ? 'border-primary bg-primary/10 text-primary font-medium'
                              : 'border-border text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {t('wallet.ruleAllow')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRuleType('block')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors ${
                            ruleType === 'block'
                              ? 'border-red-500 bg-red-500/10 text-red-500 font-medium'
                              : 'border-border text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          <Ban className="h-3.5 w-3.5" />
                          {t('wallet.ruleBlock')}
                        </button>
                      </div>
                    </div>

                    {ruleType !== 'block' && (
                      <div className="grid grid-cols-1 @sm/main:grid-cols-2 @lg/main:grid-cols-3 gap-3">
                        {[
                          { labelKey: 'wallet.autoApproveField', value: ruleAutoApprove, onChange: setRuleAutoApprove, placeholder: '0.50', hintKey: 'wallet.autoApproveHint' },
                          { labelKey: 'wallet.maxPerReqField', value: ruleMaxPerRequest, onChange: setRuleMaxPerRequest, placeholder: t('wallet.noLimit'), hintKey: 'wallet.maxPerReqHint' },
                          { labelKey: 'wallet.maxDayField', value: ruleMaxDaily, onChange: setRuleMaxDaily, placeholder: t('wallet.noLimit'), hintKey: 'wallet.maxDayHint' },
                        ].map((field) => (
                          <div key={field.labelKey} className="flex flex-col min-w-0">
                            <label className="text-xs font-medium text-foreground/80 min-h-[2rem] flex items-end pb-1.5">{t(field.labelKey)}</label>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                placeholder={field.placeholder}
                                className="w-full pl-3 pr-10 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary tabular-nums"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/50 pointer-events-none">{t('wallet.credits')}</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground/60 mt-1">{t(field.hintKey)}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {ruleFormError && (
                      <p className="text-xs text-red-500 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        {ruleFormError}
                      </p>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={handleSaveRule}
                        disabled={ruleFormSaving}
                        className="flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        {ruleFormSaving && <Spinner size="sm" />}
                        {ruleFormEditId ? t('actions.saveChanges') : t('wallet.createRule')}
                      </button>
                      <button
                        onClick={resetRuleForm}
                        className="text-xs font-medium px-4 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      >
                        {t('actions.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {overridesLoading ? (
                <div className="flex justify-center py-8"><Spinner size="xl" tone="muted" /></div>
              ) : overrides.length === 0 && !ruleFormOpen ? (
                <div className="py-10 text-center">
                  <ShieldCheck className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('wallet.noRules')}</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {t('wallet.noRulesHint')}
                  </p>
                </div>
              ) : (
                <div className="space-y-0 divide-y divide-border/50">
                  {overrides.map((override) => (
                    <div key={override.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {override.agentUsername ? `@${override.agentUsername}` : override.targetAgentId ? override.targetAgentId.slice(0, 8) + '...' : t('wallet.allAgentsFallback')}
                          </span>
                          {override.overrideType === 'block' ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-500/10 text-red-500 shrink-0">
                              {t('wallet.ruleBlocked')}
                            </span>
                          ) : (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                              override.isEnabled ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'
                            }`}>
                              {override.isEnabled ? t('wallet.ruleActive') : t('wallet.ruleDisabled')}
                            </span>
                          )}
                        </div>
                        {override.overrideType !== 'block' && (
                          <div className="flex flex-wrap gap-x-3 mt-0.5 text-xs text-muted-foreground tabular-nums">
                            <span>{t('wallet.autoApproveValue').replace('{amount}', formatCredits(override.autoApproveThresholdCredits))}</span>
                            {override.maxPerRequestCredits != null && (
                              <span>&middot; {t('wallet.maxPerReqValue').replace('{amount}', formatCredits(override.maxPerRequestCredits))}</span>
                            )}
                            {override.computeSharePct != null && (
                              <span>&middot; {t('wallet.computeShareValue').replace('{pct}', String(override.computeSharePct))}</span>
                            )}
                            {override.maxDailyCredits != null && (
                              <span>&middot; {t('wallet.maxDayValue').replace('{amount}', formatCredits(override.maxDailyCredits))}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Tip label={override.isEnabled ? t('wallet.ruleDisable') : t('wallet.ruleEnable')}>
                        <button
                          onClick={() => handleToggleOverride(override.id, !override.isEnabled)}
                          className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${
                            override.isEnabled
                              ? 'text-green-600 hover:bg-green-500/10'
                              : 'text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          {override.isEnabled ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        </button>
                        </Tip>
                        <Tip label={t('actions.edit')}>
                        <button
                          onClick={() => openEditForm(override)}
                          className="h-7 w-7 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                        </Tip>
                        <Tip label={t('actions.delete')}>
                        <button
                          onClick={() => handleDeleteOverride(override.id)}
                          className="h-7 w-7 flex items-center justify-center rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        </Tip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          )}

          {activeTab === 'referrals' && (
            <div className="space-y-6">
              <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4">{t('wallet.referralsTitle')}</h2>

                {loadingReferrals ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="xl" tone="muted" />
                  </div>
                ) : referralStats ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 @sm/main:grid-cols-2 gap-4">
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Users className="h-4 w-4" />
                          <span className="text-sm font-medium">{t('referral.totalReferrals')}</span>
                        </div>
                        <p className="text-3xl font-bold">{referralStats.totalReferrals}</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Gift className="h-4 w-4" />
                          <span className="text-sm font-medium">{t('wallet.rewardsEarned')}</span>
                        </div>
                        <p className="text-3xl font-bold text-green-500">{referralStats.completedRewards}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('wallet.revenueShareRate').replace('{rate}', referralConfig?.levels[0]?.rateLabel ?? '10%')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">{t('wallet.yourReferralLinkLabel')}</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={referralStats.shareUrl}
                          readOnly
                          className="input flex-1 bg-muted text-sm"
                        />
                        <button
                          onClick={() => copyReferralLink(referralStats.shareUrl)}
                          className="btn-primary flex items-center gap-2"
                        >
                          {copiedReferral ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          {copiedReferral ? t('referral.copied') : t('referral.copy')}
                        </button>
                      </div>
                      <p className="text-xs text-green-600 mt-2 font-medium">
                        {t('wallet.referralLinksAuto')}
                      </p>
                    </div>

                    {referralStats.recentReferrals.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium mb-3">{t('referral.recentReferrals')}</h3>
                        <div className="space-y-2">
                          {referralStats.recentReferrals.map((referral) => (
                            <div
                              key={referral.id}
                              className="flex items-center justify-between p-3 rounded-lg border border-border"
                            >
                              <div className="flex items-center gap-3">
                                <Avatar seed={referral.username} type="human" src={referral.avatar} className="w-8 h-8" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {referral.displayName || `@${referral.username}`}
                                  </p>
                                  {referral.displayName && (
                                    <p className="text-xs text-muted-foreground">@{referral.username}</p>
                                  )}
                                </div>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {new Date(referral.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gift className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('settings.unableToLoadReferrals')}</p>
                    <button
                      onClick={fetchReferralStats}
                      className="btn-secondary mt-4"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('settings.retry')}
                    </button>
                  </div>
                )}
              </div>

              <div className="card p-6">
                <h2 className="font-semibold mb-4">{t('wallet.referralHowItWorks')}</h2>
                <div className="space-y-4 text-sm">
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">1</div>
                    <div>
                      <div className="font-medium">{t('wallet.referralStep1Title')}</div>
                      <div className="text-muted-foreground">{t('wallet.referralStep1Desc')}</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">2</div>
                    <div>
                      <div className="font-medium">{t('wallet.referralStep2Title')}</div>
                      <div className="text-muted-foreground">{t('wallet.referralStep2Desc').replace('{days}', String(referralConfig?.qualificationDays ?? 30))}</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">3</div>
                    <div>
                      <div className="font-medium">{t('wallet.referralStep3Title').replace('{rate}', referralConfig?.levels[0]?.rateLabel ?? '10%')}</div>
                      <div className="text-muted-foreground">
                        {t('wallet.referralStep3Desc')
                          .replace('{days}', String(referralConfig?.qualificationDays ?? 30))
                          .replace('{type}', referralConfig?.rewardType === 'recurring' ? t('wallet.referralRecurring') : t('wallet.referralOneTime'))
                          .replace('{rate}', referralConfig?.levels[0]?.rateLabel ?? '10%')}
                      </div>
                    </div>
                  </div>
                  {(referralConfig?.levels?.length ?? 0) > 1 && (
                    <div className="flex gap-3">
                      <div className="h-6 w-6 rounded-full bg-primary/70 text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">+</div>
                      <div>
                        <div className="font-medium">{t('wallet.referralMultiLevelTitle')}</div>
                        <div className="text-muted-foreground">
                          {t('wallet.referralMultiLevelDesc').replace('{levels}', referralConfig!.levels.slice(1).map((l, i) => `${i > 0 ? ', ' : ''}${l.name}: ${l.rateLabel}`).join(''))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card p-6">
                <h2 className="font-semibold mb-3">{t('wallet.referralRewardSection')}</h2>
                <div className="space-y-2">
                  {(referralConfig?.levels ?? [{ level: 0, name: 'Direct referrals', rateLabel: '10%' }]).map((lvl) => (
                    <div key={lvl.level} className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
                      <span className="text-sm font-medium">{lvl.name}</span>
                      <span className="font-semibold text-primary">{t('wallet.revenueShareRate').replace('{rate}', lvl.rateLabel)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {t('wallet.referralRewardDesc')
                    .replace('{type}', referralConfig?.rewardType === 'recurring' ? t('wallet.referralRecurring') : t('wallet.referralOneTime'))
                    .replace('{days}', String(referralConfig?.qualificationDays ?? 30))}
                </p>
              </div>

              <details className="card group">
                <summary className="p-4 cursor-pointer flex items-center justify-between text-sm font-medium hover:bg-accent/50 rounded-lg transition-colors">
                  <span>{t('wallet.referralTermsTitle')}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-4 pb-4 text-xs text-muted-foreground space-y-3 leading-relaxed">
                  <p>{t('wallet.referralTermsEligibility')}</p>
                  <p>{t('wallet.referralTermsAttribution').replace('{days}', String(referralConfig?.qualificationDays ?? 30))}</p>
                  <p>{t('wallet.referralTermsQualification').replace('{days}', String(referralConfig?.qualificationDays ?? 30))}</p>
                  <p>{t('wallet.referralTermsReward')
                    .replace('{type}', referralConfig?.rewardType === 'recurring' ? t('wallet.referralRecurring') : t('wallet.referralOneTime'))
                    .replace('{rates}', referralConfig?.levels?.map(l => `${l.rateLabel} (${l.name.toLowerCase()})`).join(', ') ?? '10% (direct referrals)')}</p>
                  <p>{t('wallet.referralTermsDistribution')}</p>
                  <p>{t('wallet.referralTermsProhibited')}</p>
                  <p>{t('wallet.referralTermsClawback').replace('{days}', String(referralConfig?.qualificationDays ?? 30))}</p>
                  <p>{t('wallet.referralTermsTax')}</p>
                  <p>{t('wallet.referralTermsMods')}</p>
                  <p className="pt-1">
                    <Link href="/doc/referral-terms" className="text-primary hover:underline inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      {t('wallet.referralTermsLink')}
                    </Link>
                  </p>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>

      <AddCreditsDialog open={showAddCredits} onClose={() => {
        setShowAddCredits(false);
        fetchBalance();
        setActivityKey(k => k + 1);
      }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Recent Activity (small preview on overview tab)                     */
/* ------------------------------------------------------------------ */
function RecentActivity({ onViewAll }: { onViewAll: () => void }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<TransactionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/balance/transactions?limit=5')
      .then((r) => r.json())
      .then((data) => setItems(data.transactions ?? []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="card p-4 @md/main:p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">{t('wallet.recentActivity')}</h3>
        <button type="button" onClick={onViewAll} className="text-xs text-primary hover:underline">{t('wallet.viewAll')}</button>
      </div>
      <ul className="space-y-0 divide-y divide-border/50">
        {items.map((tx) => {
          const label = friendlyType(tx.type, t);
          const desc = displayDescription(tx, label);
          const isByok = BYOK_TYPE_SET.has(tx.type);
          return (
            <li key={tx.id} className="flex items-center justify-between py-2 text-sm gap-2">
              <div className="min-w-0 flex items-center gap-2">
                {isByok && (
                  <span className="text-[10px] font-semibold uppercase px-1 py-0.5 rounded bg-primary/20 text-primary shrink-0">
                    BYOK
                  </span>
                )}
                <div>
                  <span className="text-foreground font-medium">{label}</span>
                  {desc && <span className="ml-2 text-muted-foreground text-xs">&middot; {desc}</span>}
                  {EARNINGS_BALANCE_TYPE_SET.has(tx.type) && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
                      {t('wallet.chipRewards')}
                    </span>
                  )}
                </div>
              </div>
              <span className={`whitespace-nowrap font-medium flex-shrink-0 text-xs ${isByok ? 'text-muted-foreground' : tx.amountCredits >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {isByok ? '—' : `${tx.amountCredits >= 0 ? '+' : ''}${formatAmount(tx.amountCredits)}`}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
